/**
 * Stripe Configuration Module
 * 
 * Fetches Stripe publishable key from the pay platform backend
 */

import { loadStripe, Stripe } from '@stripe/stripe-js';
import { platformApi } from '@/services/platformApi';

interface StripeConfig {
  publishableKey: string;
  environment: 'development' | 'staging' | 'production';
  customerId?: string;
}

let cachedConfig: StripeConfig | null = null;

/**
 * Get Stripe configuration from backend platform
 */
export const getStripeConfig = async (forceRefresh = false): Promise<StripeConfig> => {
  // Return cached config if available and not forced to refresh
  if (cachedConfig && !forceRefresh) {
    return cachedConfig;
  }

  try {
    console.log('🔄 Fetching Stripe config from pay platform backend...');
    
    // Always fetch from the pay platform backend
    const backendConfig = await platformApi.getStripeConfig();
    const config = {
      publishableKey: backendConfig.publishableKey,
      environment: backendConfig.environment as 'development' | 'staging' | 'production',
      customerId: backendConfig.customerId
    };
    
    // Cache the result
    cachedConfig = config;
    console.log('✅ Stripe config loaded from backend successfully');
    return config;
  } catch (error) {
    console.error('❌ Failed to fetch Stripe config from backend:', error);
    throw new Error('Failed to load Stripe configuration from backend. Please ensure the pay platform is running.');
  }
};

/**
 * Validate Stripe configuration
 */
export const validateStripeConfig = (config: StripeConfig): boolean => {
  if (!config.publishableKey) {
    console.error('❌ Stripe publishable key is missing');
    return false;
  }
  
  // Check if key format is valid
  const keyPattern = /^pk_(test_|live_)[a-zA-Z0-9]{24,}$/;
  if (!keyPattern.test(config.publishableKey)) {
    console.error('❌ Invalid Stripe publishable key format');
    return false;
  }
  
  // Check environment consistency
  const isTestKey = config.publishableKey.startsWith('pk_test_');
  const isLiveKey = config.publishableKey.startsWith('pk_live_');
  
  if (config.environment === 'production' && isTestKey) {
    console.warn('⚠️ Using test Stripe key in production environment');
  }
  
  if (config.environment !== 'production' && isLiveKey) {
    console.warn('⚠️ Using live Stripe key in non-production environment');
  }
  
  const keyType = isTestKey ? 'test' : 'live';
  console.log(`✅ Stripe configured for ${config.environment} with ${keyType} key`);
  return true;
};

/**
 * Get validated Stripe publishable key (async)
 */
export const getStripePublishableKey = async (): Promise<string> => {
  const config = await getStripeConfig();
  
  if (!validateStripeConfig(config)) {
    throw new Error('Invalid Stripe configuration');
  }
  
  return config.publishableKey;
};

/**
 * Clear cached configuration (useful for testing or config changes)
 */
export const clearStripeConfigCache = (): void => {
  cachedConfig = null;
};

let stripeJsInstance: Stripe | null = null;
let stripeJsLoadPromise: Promise<Stripe> | null = null;
let stripeJsPublishableKey: string | null = null;

const isDemoPublishableKey = (key: string) =>
  key === 'pk_test_demo' || key.includes('51234567890abcdef');

async function resolvePublishableKey(): Promise<string> {
  try {
    const config = await getStripeConfig();
    if (!config.publishableKey) {
      throw new Error('Stripe publishable key not found');
    }
    return config.publishableKey;
  } catch (backendError) {
    const envKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
    if (envKey) {
      console.warn('Using VITE_STRIPE_PUBLISHABLE_KEY fallback:', backendError);
      return envKey;
    }
    throw new Error(
      'Unable to load card payments. Please check your connection and try again.'
    );
  }
}

/**
 * Load Stripe.js once and share the instance across all StripeProvider mounts.
 * Safe to call from multiple components or preload on app start.
 */
export function ensureStripeJsLoaded(): Promise<Stripe> {
  if (stripeJsInstance) {
    return Promise.resolve(stripeJsInstance);
  }
  if (stripeJsLoadPromise) {
    return stripeJsLoadPromise;
  }

  stripeJsLoadPromise = (async () => {
    const publishableKey = await resolvePublishableKey();

    if (isDemoPublishableKey(publishableKey)) {
      throw new Error(
        'Card payments are temporarily unavailable. Please try again later.'
      );
    }

    if (stripeJsInstance && stripeJsPublishableKey === publishableKey) {
      return stripeJsInstance;
    }

    const instance = await loadStripe(publishableKey);
    if (!instance) {
      throw new Error(
        'Unable to load the card form. Please check your connection and try again.'
      );
    }

    stripeJsInstance = instance;
    stripeJsPublishableKey = publishableKey;
    return instance;
  })().catch((err) => {
    stripeJsLoadPromise = null;
    throw err;
  });

  return stripeJsLoadPromise;
}

/** Fire-and-forget preload (e.g. after login or on app mount). */
export function preloadStripeJs(): void {
  ensureStripeJsLoaded().catch((err) => {
    console.warn('[Stripe] Preload failed (will retry when card UI opens):', err);
  });
}

/** Clear Stripe.js singleton so the user can retry after an error. */
export function resetStripeJsLoader(clearConfigCache = false): void {
  stripeJsInstance = null;
  stripeJsLoadPromise = null;
  stripeJsPublishableKey = null;
  if (clearConfigCache) {
    clearStripeConfigCache();
  }
}